/* ============================================================
   Dathent — PreviewContext (TASK-143, issue #236)
   Owner's conversion redesign: a new user without billing lands straight in
   the LIVE product, gets a short active-preview window, and then the whole
   /app surface blurs behind a subscribe CTA. Mounted once inside
   UpgradeProvider (needs openUpgrade for the CTA) around the routed screens.

   TASK-215 (issue #333, owner decision 2026-09-02): a payment issue
   (past_due / incomplete / unpaid) is NOT a pass any more. Those users run
   the same clock and, once it is spent, see the "payment failed — update
   your card" variant: CTA to Plan & billing, no subscribe button. The card
   gate (RequireActiveBilling) keeps letting them in — untouched, on purpose.

   Rules carried over from the guards that previously bricked the app:
     * Fail OPEN — any entitlements error/timeout means NOT a preview user.
       Paying/exempt users can never be blurred.
     * The blur is presentation only. The REAL enforcement stays server-side:
       the gateway's CARD_GATE_REQUIRED already 402s every mutation for
       non-billing users; DevTools deleting the overlay unlocks nothing.
     * Active time, not wall-clock: the timer only runs while the tab is
       visible and the user gave input in the last 30s. Elapsed persists per
       user in localStorage so a reload doesn't restart the preview.
   ============================================================ */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { useAuth } from './AuthContext';
import { useUpgrade } from './UpgradeContext.jsx';
import { usePendingOAuth } from './PendingOAuthContext.jsx';
import { previewModeEnabled, previewSeconds } from '../lib/flags.js';
import { track } from '../lib/funnel.js';
import { entitlementsAllow, isCheckoutReturn, resolvePreviewState } from '../lib/previewEntitlements.js';
import { useFreePreview } from './FreePreviewContext.jsx';

const ACTIVITY_WINDOW_MS = 30000;

const PreviewContext = createContext({ isPreview: false, locked: false, variant: 'preview' });

// Where the payment-issue CTA goes: the Plan & billing tab, which carries the
// "Payment required" alert + "Pay invoice" (screens/Settings.jsx) — the same
// destination FreeLimitsBanner uses for its "Fix payment" action.
const BILLING_PATH = '/app/settings?tab=billing';

function elapsedKey(userId) {
  return `dathent.preview.elapsed.${userId || 'anon'}`;
}

function readElapsed(userId) {
  try {
    const n = parseInt(window.localStorage.getItem(elapsedKey(userId)) ?? '', 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch (_) {
    return 0;
  }
}

function writeElapsed(userId, seconds) {
  try {
    window.localStorage.setItem(elapsedKey(userId), String(seconds));
  } catch (_) { /* storage unavailable — preview simply restarts on reload */ }
}

export function PreviewProvider({ children }) {
  const { user } = useAuth();
  // GH-1133 S5 (design §2.0 N1): inside the free preview this timed paywall
  // would blur the board after 120 s of activity — a wall on a timer, which
  // breaks the preview's principle 2. It stays inert there ("not a preview
  // user" in TASK-143's sense); everyone else is unaffected.
  const freePreviewActive = useFreePreview().active;
  const { openUpgrade } = useUpgrade();
  const { updateEntitlements } = usePendingOAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPreview, setIsPreview] = useState(false);
  const [locked, setLocked] = useState(false);
  // TASK-215: which paywall this preview user gets once the clock runs out.
  // 'preview' = no billing → subscribe CTA; 'payment_issue' = past_due /
  // incomplete / unpaid → "update your card" + Plan & billing CTA. Decided by
  // the resolver from the freshest entitlements answer; the timer is shared.
  const [variant, setVariant] = useState('preview');
  // Owner decision 2026-08-31: the paywall is SOFT — the free tier includes a
  // publication or two without a trial, so the blur must be closable. Dismiss
  // lasts one browser session (sessionStorage): the user continues on free
  // limits now, and the nudge returns on their next visit.
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem('dathent.preview.dismissed') === '1';
    } catch (_) {
      return false;
    }
  });
  const lastActivityRef = useRef(Date.now());
  const elapsedRef = useRef(0);
  const location = useLocation();
  // Hosted-checkout return (?checkout=success / ?session_id=…) — the same
  // signal RequireActiveBilling reacts to. Captured at FIRST RENDER, not in
  // the effect: Settings strips those params in its own mount effect, and
  // child effects run before ours, so by effect time the URL may already be
  // clean. A later `user` refresh (handlePaid's silent reloadUser) re-runs
  // the decision with this flag still set — harmless, billing is active by
  // then and the first poll returns.
  const [checkoutReturn] = useState(() => isCheckoutReturn(location.search));
  const getEntitlements = async () => {
    const entitlements = await api.billing.getEntitlements();
    updateEntitlements(entitlements);
    return entitlements;
  };

  // Decide per mount whether this is a preview user (TASK-195, issue #314).
  // Plain mount: one request — entitlements are cached 15s client-side, so it
  // rides the fetch RequireActiveBilling just made — plus ONE cache-bypassing
  // re-check before locking when the budget is already spent. Checkout
  // return: never lock, poll 8 × 1.5s (cache cleared each time) until the
  // Stripe webhook lands; see lib/previewEntitlements.js for the contract.
  // Every failure fails OPEN (resolver never throws; the .catch is belt and
  // braces).
  useEffect(() => {
    if (!previewModeEnabled() || !user || freePreviewActive) return;
    let cancelled = false;
    resolvePreviewState({
      getEntitlements,
      clearCache: () => api.clearCache(),
      checkoutReturn,
      elapsed: readElapsed(user?.id),
      limit: previewSeconds(),
      isCancelled: () => cancelled,
    }).then((state) => {
      if (cancelled || !state || !state.isPreview) return;
      elapsedRef.current = state.elapsed;
      setVariant(state.reason === 'payment_issue' ? 'payment_issue' : 'preview');
      setIsPreview(true);
      if (state.locked) setLocked(true);
    }).catch(() => { /* fail open: not a preview user */ });
    return () => { cancelled = true; };
  }, [user, freePreviewActive]);

  // Activity tracking + the preview clock (1s ticks while visible & active).
  useEffect(() => {
    if (!isPreview || locked) return;
    const markActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));
    const tick = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityRef.current > ACTIVITY_WINDOW_MS) return;
      elapsedRef.current += 1;
      if (elapsedRef.current % 5 === 0 || elapsedRef.current >= previewSeconds()) {
        writeElapsed(user?.id, elapsedRef.current);
      }
      if (elapsedRef.current >= previewSeconds()) setLocked(true);
    }, 1000);
    return () => {
      clearInterval(tick);
      events.forEach((e) => window.removeEventListener(e, markActivity));
    };
  }, [isPreview, locked, user?.id]);

  // While locked, watch for the subscription landing (checkout finished in the
  // upgrade modal / another tab — or, for the payment_issue variant, the card
  // updated / invoice paid in Plan & billing): re-check on focus and every 20s,
  // unlock the moment billing goes active. clearCache first — entitlements are cached 15s
  // and a stale "none" would hold the blur past a successful checkout.
  useEffect(() => {
    if (!locked) return;
    let cancelled = false;
    const recheck = async () => {
      try {
        api.clearCache();
        const ent = await getEntitlements();
        if (!cancelled && entitlementsAllow(ent)) {
          setLocked(false);
          setIsPreview(false);
        }
      } catch (_) { /* keep the blur; next poll retries */ }
    };
    const poll = setInterval(recheck, 20000);
    window.addEventListener('focus', recheck);
    return () => {
      cancelled = true;
      clearInterval(poll);
      window.removeEventListener('focus', recheck);
    };
  }, [locked]);

  // Funnel: one event per lock transition, whichever path set it (the 1s tick
  // or the already-spent check on mount).
  useEffect(() => {
    if (!locked) return;
    try { track('preview_paywall_shown', { after_seconds: elapsedRef.current, variant }); } catch { /* fail-silent */ }
    // `variant` is settled before `locked` flips (same resolver callback), so
    // it is a label here, not a second trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  const hide = () => {
    try {
      window.sessionStorage.setItem('dathent.preview.dismissed', '1');
    } catch (_) { /* worst case: the blur returns after a reload */ }
    setDismissed(true);
  };

  const dismiss = () => {
    try { track('preview_paywall_dismissed', { variant }); } catch { /* fail-silent */ }
    hide();
  };

  // payment_issue CTA. The blur is fixed-position over the WHOLE /app surface,
  // Settings included, so Plan & billing would open underneath it: hide for
  // this session first (same soft policy as the × ), then navigate. The
  // locked re-check above keeps polling and lifts the preview for good once
  // the card is updated / the invoice paid.
  const fixPayment = () => {
    try { track('preview_payment_fix_clicked', {}); } catch { /* fail-silent */ }
    hide();
    navigate(BILLING_PATH);
  };

  const paymentVariant = variant === 'payment_issue';

  return (
    <PreviewContext.Provider value={{ isPreview, locked, variant }}>
      {children}
      {locked && !dismissed && (
        <div className="preview-paywall" role="dialog" aria-modal="true" aria-label={t(paymentVariant ? 'previewPaywall.paymentTitle' : 'previewPaywall.title')}>
          <div className="preview-paywall__card">
            <button
              type="button"
              className="preview-paywall__close"
              aria-label={t('previewPaywall.close')}
              onClick={dismiss}
            >
              ×
            </button>
            {paymentVariant ? (
              /* TASK-215: payment failed — update the card. No subscribe button:
                 the subscription exists, it is the invoice that is open. */
              <>
                <div className="preview-paywall__eyebrow preview-paywall__eyebrow--danger">{t('previewPaywall.paymentEyebrow')}</div>
                <h2 className="preview-paywall__title">{t('previewPaywall.paymentTitle')}</h2>
                <p className="preview-paywall__sub">{t('previewPaywall.paymentSub')}</p>
                <button type="button" className="preview-paywall__cta" onClick={fixPayment}>
                  {t('previewPaywall.paymentCta')}
                </button>
                <div className="preview-paywall__note">{t('previewPaywall.paymentNote')}</div>
              </>
            ) : (
              <>
                <div className="preview-paywall__eyebrow">{t('previewPaywall.eyebrow')}</div>
                <h2 className="preview-paywall__title">{t('previewPaywall.title')}</h2>
                <p className="preview-paywall__sub">{t('previewPaywall.sub')}</p>
                <button
                  type="button"
                  className="preview-paywall__cta"
                  onClick={() => {
                    try { track('preview_subscribe_clicked', {}); } catch { /* fail-silent */ }
                    openUpgrade({ feature: 'preview' });
                  }}
                >
                  {t('previewPaywall.cta')}
                </button>
                <button type="button" className="preview-paywall__free" onClick={dismiss}>
                  {t('previewPaywall.continueFree')}
                </button>
                <div className="preview-paywall__note">{t('previewPaywall.note')}</div>
              </>
            )}
          </div>
        </div>
      )}
    </PreviewContext.Provider>
  );
}

export function usePreview() {
  return useContext(PreviewContext);
}

export default PreviewProvider;
